import type { ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { cx } from '@/lib/cx';
import { isUpgradeable, planGateCopy } from '@/lib/planGates';
import { Icon } from './Icon';
import s from './LockedState.module.css';

/**
 * THE locked-state component — every lock in the app uses it (web_app_design.md §2): why it is
 * locked, what can be done on the current plan, and where to upgrade: the plans page, which
 * subscribes through the web checkout (§2).
 */
export function LockedState({
  reason,
  message,
  alternative,
  compact,
  inline,
}: {
  /** The backend's reason code (`plan_timeframe_allowlist`, …). */
  reason?: string | null;
  /** The server's message, used when the code has no local copy. */
  message?: string;
  /** "What you can do now" — a same-plan alternative. */
  alternative?: ReactNode;
  compact?: boolean;
  /** One line, for table rows and pickers. */
  inline?: boolean;
}) {
  const why = planGateCopy(reason, message ?? 'This is not included in your plan.');
  const upgradeable = isUpgradeable(reason);

  if (inline) {
    return (
      <span className={s.inline} title={why}>
        <Icon name="lock" size={14} />
        {why}
      </span>
    );
  }

  return (
    <div className={cx(s.locked, compact && s.compact)} role="note">
      <div className={s.head}>
        <span className={s.icon}>
          <Icon name={upgradeable ? 'lock' : 'block'} size={18} />
        </span>
        <span className={s.why}>{why}</span>
      </div>
      {alternative && (
        <div className={s.row}>
          <span className={s.label}>What you can do now</span>
          <span className={s.text}>{alternative}</span>
        </div>
      )}
      {upgradeable && (
        <div className={s.actions}>
          <Link to="/plans" className={s.upgrade}>
            <Icon name="workspace_premium" size={16} />
            Compare plans
          </Link>
          <span className={s.note}>Subscribe here or in the SixOne8 phone app.</span>
        </div>
      )}
    </div>
  );
}
